/**
 * Member Slot Service
 * Manages purchasing additional team member slots for business plans
 */

import { supabase } from '../lib/supabaseClient';
import { iapService, PRODUCT_IDS, type ProductId } from './iapService';

export interface MemberSlotPurchase {
  id: string;
  created_at: string;
  organization_id: string;
  requested_by: string;
  slots_requested: number;
  cost_gbp: number;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  stripe_payment_intent_id: string | null;
  stripe_invoice_id: string | null;
  approved_at: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  notes: string | null;
}

export interface MemberSlotInfo {
  baseSlots: number;
  purchasedSlots: number;
  pendingSlots: number;
  totalSlots: number;
  currentMembers: number;
  availableSlots: number;
  canAddMore: boolean;
}

export const COST_PER_SLOT_GBP = 49; // £49 per additional slot (manual payment request)
export const COST_PER_SLOT_IAP_GBP = 59; // £59 per additional slot via Apple IAP

// Slot tier pricing and slot counts
export const SLOT_TIERS = {
  SINGLE: {
    productId: PRODUCT_IDS.TEAM_MEMBER_SLOT,
    slots: 1,
    price: 59,
    displayName: 'Team Slot',
    description: '+1 Member'
  },
  DOUBLE: {
    productId: PRODUCT_IDS.TEAM_MEMBER_SLOT_2,
    slots: 2,
    price: 100,
    displayName: 'Team Slot +2',
    description: '+2 Members'
  },
  FIVE: {
    productId: PRODUCT_IDS.TEAM_MEMBER_SLOT_5,
    slots: 5,
    price: 500,
    displayName: 'Team Slot +5',
    description: '+5 Members'
  }
} as const;

/**
 * Get member slot information for an organization
 */
export const getMemberSlotInfo = async (organizationId: string): Promise<MemberSlotInfo> => {
  try {
    // Get organization data
    const { data: org, error: orgError } = await supabase
      .from('organizations')
      .select('max_members, additional_members_purchased, additional_members_pending')
      .eq('id', organizationId)
      .single();

    if (orgError) throw orgError;

    // Get current member count using RPC function
    const { data: currentCount, error: countError } = await supabase
      .rpc('get_current_member_count', { p_organization_id: organizationId });

    if (countError) throw countError;

    const baseSlots = org.max_members || 0;
    const purchasedSlots = org.additional_members_purchased || 0;
    const pendingSlots = org.additional_members_pending || 0;
    const totalSlots = baseSlots + purchasedSlots;
    const currentMembers = currentCount || 0;
    const availableSlots = totalSlots - currentMembers;

    return {
      baseSlots,
      purchasedSlots,
      pendingSlots,
      totalSlots,
      currentMembers,
      availableSlots,
      canAddMore: availableSlots > 0
    };
  } catch (error) {
    console.error('[MemberSlotService] Error getting slot info:', error);
    throw error;
  }
};

/**
 * Request additional member slots (creates pending purchase)
 */
export const requestAdditionalSlots = async (
  organizationId: string,
  slotsRequested: number
): Promise<MemberSlotPurchase> => {
  try {
    if (slotsRequested < 1) {
      throw new Error('Must request at least 1 slot');
    }

    const costGbp = slotsRequested * COST_PER_SLOT_GBP;

    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Create purchase request
    const { data, error } = await supabase
      .from('member_slot_purchases')
      .insert({
        organization_id: organizationId,
        requested_by: user.id,
        slots_requested: slotsRequested,
        cost_gbp: costGbp,
        status: 'pending'
      })
      .select()
      .single();

    if (error) throw error;

    // Update organization pending count
    const { error: updateError } = await supabase
      .from('organizations')
      .update({
        additional_members_pending: supabase.rpc('increment', { x: slotsRequested })
      })
      .eq('id', organizationId);

    if (updateError) {
      console.error('[MemberSlotService] Failed to update pending count:', updateError);
    }

    console.log(`[MemberSlotService] ✅ Requested ${slotsRequested} slot(s) for £${costGbp}`);
    return data;
  } catch (error) {
    console.error('[MemberSlotService] Error requesting slots:', error);
    throw error;
  }
};

/**
 * Get purchase history for an organization
 */
export const getPurchaseHistory = async (organizationId: string): Promise<MemberSlotPurchase[]> => {
  try {
    const { data, error } = await supabase
      .from('member_slot_purchases')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('[MemberSlotService] Error getting purchase history:', error);
    throw error;
  }
};

/**
 * Get pending purchase requests for an organization
 */
export const getPendingPurchases = async (organizationId: string): Promise<MemberSlotPurchase[]> => {
  try {
    const { data, error } = await supabase
      .from('member_slot_purchases')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('[MemberSlotService] Error getting pending purchases:', error);
    throw error;
  }
};

/**
 * Cancel a pending purchase request
 */
export const cancelPurchaseRequest = async (purchaseId: string): Promise<void> => {
  try {
    // Get purchase details first
    const { data: purchase, error: getError } = await supabase
      .from('member_slot_purchases')
      .select('organization_id, slots_requested, status')
      .eq('id', purchaseId)
      .single();

    if (getError) throw getError;
    if (purchase.status !== 'pending') {
      throw new Error('Can only cancel pending purchases');
    }

    // Update purchase status
    const { error: updateError } = await supabase
      .from('member_slot_purchases')
      .update({ status: 'cancelled' })
      .eq('id', purchaseId);

    if (updateError) throw updateError;

    // Decrease pending count
    const { error: orgError } = await supabase.rpc('decrement_pending_slots', {
      org_id: purchase.organization_id,
      slots: purchase.slots_requested
    });

    if (orgError) {
      console.error('[MemberSlotService] Failed to update pending count:', orgError);
    }

    console.log('[MemberSlotService] ✅ Cancelled purchase request');
  } catch (error) {
    console.error('[MemberSlotService] Error cancelling purchase:', error);
    throw error;
  }
};

/**
 * Check if organization can add a member (has available slots)
 */
export const canAddMember = async (organizationId: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .rpc('can_add_member', { org_id: organizationId });

    if (error) throw error;
    return data === true;
  } catch (error) {
    console.error('[MemberSlotService] Error checking member capacity:', error);
    return false;
  }
};

/**
 * Get slot tier info from product ID
 */
const getSlotTierFromProductId = (productId: string) => {
  return Object.values(SLOT_TIERS).find(tier => tier.productId === productId);
};

/**
 * Purchase additional team member slot via Apple IAP
 * Supports multiple slot tiers (1, 2, or 5 slots)
 */
export const purchaseSlotViaIAP = async (
  organizationId: string, 
  productId: string = PRODUCT_IDS.TEAM_MEMBER_SLOT
): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('[MemberSlotService] Initiating IAP purchase for:', productId);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get slot tier info
    const tier = getSlotTierFromProductId(productId);
    if (!tier) {
      return { success: false, error: 'Invalid product ID' };
    }

    // Check if this slot type has already been purchased
    const { data: org, error: orgFetchError } = await supabase
      .from('organizations')
      .select('purchased_slot_subscriptions, additional_members_purchased')
      .eq('id', organizationId)
      .single();

    if (orgFetchError) throw orgFetchError;

    const purchasedSlots = org.purchased_slot_subscriptions || [];
    if (purchasedSlots.includes(productId)) {
      return { success: false, error: 'This slot tier has already been purchased. Cancel the existing subscription to purchase again.' };
    }

    // Initialize IAP service
    await iapService.initialize();

    // Purchase the slot subscription
    const result = await iapService.purchase(productId as ProductId);

    if (!result.success) {
      return { success: false, error: result.error || 'Purchase failed' };
    }

    console.log('[MemberSlotService] ✅ IAP purchase successful, updating database...');

    // Update organization: add slots and track this subscription
    const updatedPurchasedSlots = [...purchasedSlots, productId];
    const { error: updateError } = await supabase
      .from('organizations')
      .update({
        additional_members_purchased: (org.additional_members_purchased || 0) + tier.slots,
        purchased_slot_subscriptions: updatedPurchasedSlots
      })
      .eq('id', organizationId);

    if (updateError) {
      console.error('[MemberSlotService] Failed to update organization:', updateError);
      throw updateError;
    }

    // Record the purchase in member_slot_purchases table
    await supabase
      .from('member_slot_purchases')
      .insert({
        organization_id: organizationId,
        requested_by: user.id,
        slots_requested: tier.slots,
        cost_gbp: tier.price,
        status: 'approved', // IAP purchases are immediately approved
        approved_at: new Date().toISOString(),
        notes: `Apple IAP Purchase (${tier.displayName}) - Transaction ID: ${result.transactionId || 'N/A'}`
      });

    console.log(`[MemberSlotService] ✅ ${tier.slots} team member slot(s) added successfully`);
    return { success: true };
  } catch (error: any) {
    console.error('[MemberSlotService] Error purchasing slot via IAP:', error);
    return { success: false, error: error.message || 'Failed to purchase slot' };
  }
};

export default {
  getMemberSlotInfo,
  requestAdditionalSlots,
  getPurchaseHistory,
  getPendingPurchases,
  cancelPurchaseRequest,
  canAddMember,
  purchaseSlotViaIAP,
  COST_PER_SLOT_GBP,
  COST_PER_SLOT_IAP_GBP,
  SLOT_TIERS
};
